import React from 'react';
import { Plug, Cloud, Server, Laptop, ArrowRight, Check, Workflow, Database, Globe, Smartphone } from 'lucide-react';

const IntegrationSection = () => {
  const llmOptions = [
    {
      icon: Server,
      name: "Local LLM (Ollama)",
      description: "Tamamen on-premise çözüm. Verileriniz asla dışarı çıkmaz.",
      features: ["Gizlilik garantisi", "İnternet gerektirmez", "Özelleştirilebilir modeller"],
      highlight: "Kurumsal Gizlilik"
    },
    {
      icon: Cloud,
      name: "OpenAI GPT",
      description: "En gelişmiş dil modelleri ile maksimum performans.",
      features: ["GPT-4o desteği", "Yüksek doğruluk", "Hızlı yanıt"],
      highlight: "Yüksek Performans"
    },
    {
      icon: Globe,
      name: "Google Gemini",
      description: "Google'ın çok modlu AI teknolojisi.",
      features: ["Görsel anlama", "Çok dilli destek", "Uzun bağlam"],
      highlight: "Çok Modlu"
    }
  ];

  const integrationPoints = [
    { icon: Database, title: "Veritabanı", desc: "SQL Server, PostgreSQL, MySQL, Oracle" },
    { icon: Globe, title: "Web Kaynakları", desc: "Web sayfaları ve API entegrasyonu" },
    { icon: Workflow, title: "İş Akışları", desc: "Mevcut sistemlerinize bağlantı" },
    { icon: Smartphone, title: "Mobil Erişim", desc: "Her cihazdan güvenli erişim" }
  ];

  return (
    <section id="integrations" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-violet-100 text-violet-700 text-sm font-semibold mb-4">
            Entegrasyonlar
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            Kolay Entegrasyon,
            <span className="text-blue-600"> Sınırsız Olanak</span>
          </h2>
          <p className="max-w-3xl mx-auto text-lg text-slate-600">
            Dokümanlarınızı mevcut sistemlerinize kolayca entegre edin. 
            İster bulut, ister on-premise - CAPSNA her ortamda çalışır.
          </p>
        </div>

        {/* LLM Options */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-slate-800 text-center mb-8">
            AI Model Seçenekleri - <span className="text-blue-600">Sizin Tercihiniz</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {llmOptions.map((option, idx) => (
              <div
                key={idx}
                className="group relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl hover:border-blue-300 transition-all duration-300"
              >
                {/* Highlight Badge */}
                <div className="absolute -top-3 left-6">
                  <span className="px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-semibold">
                    {option.highlight}
                  </span>
                </div>

                <div className="pt-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center mb-4 shadow-lg">
                    <option.icon className="w-7 h-7 text-white" />
                  </div>

                  <h4 className="text-xl font-bold text-slate-800 mb-2">{option.name}</h4>
                  <p className="text-slate-600 mb-4">{option.description}</p>

                  <ul className="space-y-2">
                    {option.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Integration Points */}
        <div className="bg-gradient-to-r from-blue-600 to-violet-600 rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Her Ortama Entegre Edin
              </h3>
              <p className="text-blue-100 mb-6 text-lg">
                CAPSNA, mevcut iş süreçlerinize sorunsuz entegre olur. 
                Dokümanlarınızı veritabanlarınızla, web kaynaklarınızla ve 
                iş akışlarınızla birleştirin.
              </p>
              <ul className="space-y-3 text-white">
                <li className="flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-300" />
                  API ile tam entegrasyon
                </li>
                <li className="flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-300" />
                  SSO ve LDAP/AD desteği
                </li>
                <li className="flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-blue-300" />
                  Webhook ve event-driven mimari
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {integrationPoints.map((point, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-colors"
                >
                  <point.icon className="w-8 h-8 text-white mb-3" />
                  <h4 className="text-white font-semibold mb-1">{point.title}</h4>
                  <p className="text-blue-200 text-sm">{point.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationSection;
